/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

import { compose, not } from '@accelint/core';
import { isNothing } from '../is-nothing';

/**
 * Determines if the given value is **not** undefined or null.
 *
 * @param val - The value to check whether or not it is not null or undefined.
 * @returns True if the value is not null or undefined, false otherwise.
 *
 * @example
 * ```typescript
 * import { isSomething } from '@accelint/predicates/is-something';
 *
 * if (isSomething(val)) {
 *   // happy path...
 * }
 *
 * isSomething(0);         // true
 * isSomething('');        // true
 * isSomething(false);     // true
 * isSomething(null);      // false
 * isSomething(undefined); // false
 * ```
 */
export const isSomething = compose(not, isNothing);
